// AI Context for managing AI state and interactions
import React, { createContext, useContext, useState, useCallback } from 'react';
import aiService from '../services/aiService';

const AIContext = createContext();

export const useAI = () => {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAI must be used within an AIProvider');
  }
  return context;
};

export const AIProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [userProfile, setUserProfile] = useState({
    level: 'beginner',
    interests: [],
    goals: [],
    progress: {}
  });

  const handleAIRequest = useCallback(async (requestFunction, ...args) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await requestFunction(...args);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getTutorResponse = useCallback(async (userMessage, context = {}) => {
    return await handleAIRequest(aiService.getTutorResponse, userMessage, {
      ...context,
      userProfile,
      conversationHistory: conversationHistory.slice(-5) // Last 5 messages for context
    });
  }, [handleAIRequest, userProfile, conversationHistory]);

  const analyzePronunciation = useCallback(async (word, userAttempt, context = {}) => {
    return await handleAIRequest(aiService.analyzePronunciation, word, userAttempt, {
      ...context,
      userProfile
    });
  }, [handleAIRequest, userProfile]);

  const generateExercises = useCallback(async (word, difficulty = 'beginner', exerciseType = 'vocabulary') => {
    return await handleAIRequest(aiService.generateExercises, word, difficulty, exerciseType);
  }, [handleAIRequest]);

  const generateLearningPath = useCallback(async () => {
    return await handleAIRequest(aiService.generateLearningPath, 
      userProfile.level, 
      userProfile.interests, 
      userProfile.goals
    );
  }, [handleAIRequest, userProfile]);

  const correctGrammar = useCallback(async (text, context = {}) => {
    return await handleAIRequest(aiService.correctGrammar, text, context);
  }, [handleAIRequest]);

  const explainVocabulary = useCallback(async (word, context = {}) => {
    return await handleAIRequest(aiService.explainVocabulary, word, {
      ...context,
      userProfile
    });
  }, [handleAIRequest, userProfile]);

  const generateConversation = useCallback(async (topic, level = 'beginner') => {
    return await handleAIRequest(aiService.generateConversation, topic, level);
  }, [handleAIRequest]);

  const analyzeProgress = useCallback(async (userData, timePeriod = 'week') => {
    return await handleAIRequest(aiService.analyzeProgress, userData, timePeriod);
  }, [handleAIRequest]);

  const addToConversation = useCallback((userMessage, aiResponse) => {
    setConversationHistory(prev => [
      ...prev,
      { type: 'user', content: userMessage, timestamp: new Date() },
      { type: 'ai', content: aiResponse, timestamp: new Date() }
    ]);
  }, []);

  const updateUserProfile = useCallback((updates) => {
    setUserProfile(prev => ({ ...prev, ...updates }));
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearConversation = useCallback(() => {
    setConversationHistory([]);
  }, []);

  const value = {
    // State
    isLoading,
    error,
    conversationHistory,
    userProfile,
    
    // AI Functions
    getTutorResponse,
    analyzePronunciation,
    generateExercises,
    generateLearningPath,
    correctGrammar,
    explainVocabulary,
    generateConversation,
    analyzeProgress,
    
    // Utility Functions
    addToConversation,
    updateUserProfile,
    clearError,
    clearConversation
  };

  return (
    <AIContext.Provider value={value}>
      {children}
    </AIContext.Provider>
  );
};
